import React, { useEffect, useState } from 'react';
import { Modal, Spin, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resumeBookingPayment } from '../services/BookingService';
import { BookingPaymentStep } from './HeavyDutyMachineryBookingWidget';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface ResumePaymentModalProps {
  bookingId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Re-opens HeavyDutyMachineryBookingWidget's own payment step for a Pending booking
 * whose guest never finished paying the first time -- see
 * BookingController.ResumePayment for why this can't just call createBooking
 * again (that would create a second Booking + PaymentIntent for the same
 * slot instead of resuming this one). Fetches a fresh clientSecret on open,
 * same shape createBooking's own response carries.
 */
const ResumePaymentModal: React.FC<ResumePaymentModalProps> = ({ bookingId, open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    setClientSecret(undefined);
    setError(undefined);
    resumeBookingPayment(bookingId)
      .then((booking) => {
        if (booking.clientSecret) setClientSecret(booking.clientSecret);
        else setError(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_RESUME_PAYMENT_ERROR));
      })
      .catch((err: any) => setError(err?.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_RESUME_PAYMENT_ERROR)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId]);

  return (
    <Modal open={open} onCancel={onClose} footer={null} destroyOnClose>
      {error && <Alert type="error" showIcon message={error} style={{ borderRadius: 14 }} />}
      {!error && !clientSecret && (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin size="large" />
        </div>
      )}
      {clientSecret && (
        <BookingPaymentStep
          clientSecret={clientSecret}
          onPaid={() => {
            onClose();
            navigate(`/heavy-duty-machinery/booking-confirming?bookingId=${bookingId}`);
          }}
        />
      )}
    </Modal>
  );
};

export default ResumePaymentModal;
